import { runConstraintPipeline } from '../../core/constraint-pipeline.js';
import type { BarState } from '../../extensions/types.js';
import type { GanttSingleView } from '../../gantt-view-manager.js';
import type { LineEndpoints } from './dependency-line.js';
import type { GanttLinkPort } from './link-port.js';

export const LINK_CLICK_THRESHOLD_PX = 3;

/**
 * A target that the cursor has magnetized onto. The session snaps the
 * preview endpoint to {@link clientX}/{@link clientY} (the target
 * handle's center in client coordinates) so the arrow visibly latches
 * onto the handle, and prefers {@link rowId} over any DOM-hit result.
 */
export interface LinkMagnetTarget {
  readonly rowId: string;
  readonly clientX: number;
  readonly clientY: number;
}

export interface LinkDragSessionConfig {
  readonly view: GanttSingleView;
  readonly target: HTMLElement;
  readonly pointerEvent: PointerEvent;
  readonly fromRowId: string;
  readonly fromBarState: BarState;
  /** Starting pixel position of the arrow (typically the bar's right edge). */
  readonly originPx: { readonly clientX: number; readonly clientY: number };
  /** Resolves the rowId currently hovered under the pointer (via DOM). */
  readonly resolveRowAt: (clientX: number, clientY: number) => string | null;
  /**
   * Optional geometric magnet. Called on every pointermove; when it
   * returns a target, the session snaps the preview endpoint to that
   * handle's center and prefers the magnet rowId over the DOM hit.
   * Side-effect-friendly: implementations typically also update a
   * shared signal so the bar renderer can grow the matching handle.
   */
  readonly resolveMagnet?: (
    clientX: number,
    clientY: number
  ) => LinkMagnetTarget | null;
  readonly port: GanttLinkPort;
  /** Live endpoints for the pending arrow, client-space. */
  readonly onPreview: (endpoints: LineEndpoints | null) => void;
  /** Surfaces the constraint warning to the drag tooltip (if any). */
  readonly onWarning?: (warningKey: string | undefined) => void;
}

/**
 * Owns a pointerdown→pointerup link-drag. Mirrors {@link DragSession}
 * but the "commit" writes a dependency edge via the injected port
 * instead of updating start/end cells. Cycle-rejection is enforced at
 * pointerup by running the proposal through the constraint pipeline
 * with `intent === 'link'`.
 */
export class LinkDragSession {
  private readonly startClientX: number;
  private readonly startClientY: number;
  private movedBeyondThreshold = false;
  private currentTargetRowId: string | null = null;
  private latestWarning: string | undefined;
  private readonly capturedPointerId: number;
  private readonly removeListeners: () => void;

  constructor(private readonly cfg: LinkDragSessionConfig) {
    this.startClientX = cfg.pointerEvent.clientX;
    this.startClientY = cfg.pointerEvent.clientY;
    this.capturedPointerId = cfg.pointerEvent.pointerId;

    cfg.pointerEvent.preventDefault();
    cfg.pointerEvent.stopPropagation();

    try {
      cfg.target.setPointerCapture(this.capturedPointerId);
    } catch {
      // Capture can fail when the element detaches mid-event; we still
      // rely on stopPropagation to block Edgeless pan-tool.
    }

    const onMove = (e: PointerEvent) => this.handleMove(e);
    const onUp = (e: PointerEvent) => this.handleUp(e);
    const onCancel = (e: PointerEvent) => this.handleCancel(e);
    cfg.target.addEventListener('pointermove', onMove);
    cfg.target.addEventListener('pointerup', onUp);
    cfg.target.addEventListener('pointercancel', onCancel);

    this.removeListeners = () => {
      cfg.target.removeEventListener('pointermove', onMove);
      cfg.target.removeEventListener('pointerup', onUp);
      cfg.target.removeEventListener('pointercancel', onCancel);
    };
  }

  private handleMove(evt: PointerEvent): void {
    if (evt.pointerId !== this.capturedPointerId) return;
    evt.preventDefault();
    evt.stopPropagation();

    const dx = evt.clientX - this.startClientX;
    const dy = evt.clientY - this.startClientY;
    if (!this.movedBeyondThreshold) {
      if (Math.hypot(dx, dy) < LINK_CLICK_THRESHOLD_PX) return;
      this.movedBeyondThreshold = true;
    }

    // Magnet wins over the DOM hit — a user aiming roughly at a
    // handle should get that handle, even if the cursor isn't
    // directly over the bar body yet. Falls through to plain DOM
    // hit-testing when no magnet exists.
    const magnet = this.cfg.resolveMagnet?.(evt.clientX, evt.clientY) ?? null;
    this.currentTargetRowId =
      magnet?.rowId ?? this.cfg.resolveRowAt(evt.clientX, evt.clientY);

    if (
      this.currentTargetRowId &&
      this.currentTargetRowId !== this.cfg.fromRowId
    ) {
      const result = runConstraintPipeline(
        this.cfg.fromBarState,
        this.cfg.view.extensions.dragConstraints.list(),
        {
          view: this.cfg.view,
          intent: 'link',
          previousState: this.cfg.fromBarState,
          linkTargetRowId: this.currentTargetRowId,
        }
      );
      this.latestWarning = result.allowed ? undefined : result.warning;
    } else {
      this.latestWarning = undefined;
    }
    this.cfg.onWarning?.(this.latestWarning);

    // Snap the preview to the magnetized handle's center so the arrow
    // visibly latches on — otherwise the line would float to the raw
    // cursor even though the commit target is already decided.
    this.cfg.onPreview({
      fromX: this.cfg.originPx.clientX,
      fromY: this.cfg.originPx.clientY,
      toX: magnet?.clientX ?? evt.clientX,
      toY: magnet?.clientY ?? evt.clientY,
    });
  }

  private handleUp(evt: PointerEvent): void {
    if (evt.pointerId !== this.capturedPointerId) return;
    evt.preventDefault();
    evt.stopPropagation();
    this.cleanup();

    if (!this.movedBeyondThreshold) return;
    const target = this.currentTargetRowId;
    if (!target || target === this.cfg.fromRowId) return;
    if (this.latestWarning) return; // Constraint rejected — abort silently.

    void this.cfg.port.createLink(this.cfg.fromRowId, target);
  }

  private handleCancel(evt: PointerEvent): void {
    if (evt.pointerId !== this.capturedPointerId) return;
    this.cleanup();
  }

  private cleanup(): void {
    try {
      this.cfg.target.releasePointerCapture(this.capturedPointerId);
    } catch {
      // Already released; safe to ignore.
    }
    this.removeListeners();
    this.cfg.onPreview(null);
    this.cfg.onWarning?.(undefined);
  }
}

export function startLinkDragSession(
  cfg: LinkDragSessionConfig
): LinkDragSession {
  return new LinkDragSession(cfg);
}
